/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { SPACE_DEFAULT_TITLE } from '@gitbook/app-database';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { Description, EmptyState, EmptyStateErrorHeader } from '@gitbook/spine-react';
import { NoCaptureErrorBoundary, reportException } from '@gitbook/util-debug';
import { assertNever } from '@gitbook/util-ts';

import { PageIcon, SpaceIcon } from '../..';
import { useNavigation } from '../../../navigation';
import {
    type AskGitBookSearchSuggestion,
    type AskGitBookSearchSuggestionGroup,
    useAskGitBookSuggestion,
} from '../../AISearch';
import {
    type ContentSearchGroupSpace,
    type ContentSearchResult,
    SearchModalResults,
    type SearchResultGroupInfo,
    useContentSearchResults,
} from '../../Search';

type SearchResultItem = ContentSearchResult | AskGitBookSearchSuggestion;

type AppSearchResultsProps = {
    query: string;
    searchQuery: string;
    onSubmitAIQuery: (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => void;
    organization: string | null;
    hasAI?: boolean;
};

/**
 * For a non-empty search query, fetch the results and display them.
 */
export function AppSearchResults(props: AppSearchResultsProps): React.ReactElement {
    return (
        <NoCaptureErrorBoundary
            resetKeys={[props.query, props.searchQuery]}
            onError={(error) =>
                reportException(error, {
                    tags: { area: 'app-search-results' },
                    extra: { organizationId: props.organization, query: props.searchQuery },
                })
            }
            fallbackRender={() => (
                <EmptyState>
                    <EmptyStateErrorHeader />
                    <Description>Search results couldn't be displayed.</Description>
                </EmptyState>
            )}
        >
            <AppSearchResultsContent {...props} />
        </NoCaptureErrorBoundary>
    );
}

function AppSearchResultsContent(props: AppSearchResultsProps): React.ReactElement {
    const { onSubmitAIQuery, hasAI, organization } = props;
    const query = React.useDeferredValue(props.query);

    const { navigate } = useNavigation();
    const askGitBook = useAskGitBookSuggestion(query, onSubmitAIQuery, hasAI);
    const searchQuery = React.useDeferredValue(props.searchQuery);
    const searchResults = useContentSearchResults({
        organizationId: organization,
        query,
        searchQuery,
    });

    // Order results:
    //  1. ask-gitbook suggestion
    //  2. spaces
    //  3. pages
    //  4. sections (i.e. actual content matches, grouped by space)
    const results = React.useMemo(() => {
        const spaces = searchResults.filter((item) => item.type === 'space');
        const pages = searchResults.filter((item) => item.type === 'page' || item.type === 'link');
        const sections = searchResults.filter((item) => item.type === 'section');

        return [
            ...(askGitBook.suggestion ? [askGitBook.suggestion] : []),
            ...spaces,
            ...pages,
            ...sections,
        ];
    }, [searchResults, askGitBook.suggestion]);

    const renderItem = React.useCallback(
        (item: SearchResultItem) => {
            if (item.type === 'ask-gitbook') {
                return askGitBook.onRenderSuggestion(item);
            }
            switch (item.type) {
                case 'space': {
                    return {
                        title: item.title,
                        icon: <SpaceIcon space={item.space} size={26} />,
                        action: 'Open space',
                    };
                }
                case 'page':
                    return {
                        title: item.title,
                        subtitle: item.path,
                        icon: <PageIcon page={item.page} size={20} />,
                        action: 'Open page',
                    };
                case 'link':
                    return {
                        title: item.title,
                        subtitle: item.url,
                        icon: <PageIcon page={item.page} size={20} />,
                        action: 'Open link',
                    };
                case 'section': {
                    // For search results pointing to content within sections, we show a breadcrumbs UI with the page title and the section title.
                    const pageTitle = item.page.title;
                    const sectionTitle = item.title;

                    return {
                        title: (
                            <span className="inline-flex items-center gap-1">
                                <PageIcon page={item.page} size={10} />
                                {sectionTitle ? (
                                    <>
                                        <span className="text-ui-small text-neutral-low">
                                            {pageTitle} /
                                        </span>
                                        <span className="text-ui-small text-neutral-high">
                                            {sectionTitle}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-ui-small text-neutral-high">
                                        {pageTitle}
                                    </span>
                                )}
                            </span>
                        ),
                        body: item.body,
                        action: 'Go to section',
                    };
                }
                default:
                    assertNever(item);
            }
        },
        [askGitBook.onRenderSuggestion]
    );

    const renderGroup = React.useCallback(
        (group: ContentSearchGroupSpace | undefined): SearchResultGroupInfo | undefined => {
            if (typeof group === 'string') {
                return {
                    title: group,
                };
            }

            if (group?.type === 'space') {
                return {
                    title: 'Matched content in',
                    icon: <SpaceIcon space={group.space} size={18} />,
                    suffix: group.space.title || SPACE_DEFAULT_TITLE,
                };
            }

            return undefined;
        },
        []
    );

    const onOpen = React.useCallback(
        (item: SearchResultItem, index: number) => {
            if (item.type === 'ask-gitbook') {
                return askGitBook.onOpenSuggestion(item);
            }

            analytics.logEvent({
                type: 'search_open_result',
                // Link results come from link pages.
                resultType: item.type === 'link' ? 'page' : item.type,
                resultIndex: index,
            });

            switch (item.type) {
                case 'space': {
                    navigate({ screen: getScreenForContentSearchResult(item) });
                    break;
                }
                case 'page':
                    navigate({ screen: getScreenForContentSearchResult(item) });
                    break;
                case 'link':
                    window.open(item.url, '_blank');
                    break;
                case 'section':
                    navigate({
                        screen: {
                            ...getScreenForContentSearchResult(item),
                            hash: item.anchor,
                        },
                    });
                    break;
                default:
                    assertNever(item);
            }
        },
        [askGitBook.onOpenSuggestion, navigate]
    );

    return (
        <>
            <SearchModalResults
                data={results}
                getKey={getKey}
                renderItem={renderItem}
                renderGroup={renderGroup}
                groupItem={groupSearchItem}
                getGroupKey={getGroupKey}
                onOpen={onOpen}
                query={query}
                className="px-4 pt-2 pb-1"
            />
            {searchResults.length === 0 ? (
                <EmptyState>
                    <Description>No search results for "{query}"</Description>
                </EmptyState>
            ) : null}
        </>
    );
}

/**
 * Resolve the app screen to open for a content search result.
 */
export function getScreenForContentSearchResult(
    item: Exclude<ContentSearchResult, { type: 'link' }>
): NavigationScreenSpaceState {
    switch (item.type) {
        case 'space':
            return {
                type: 'space',
                space: item.space.id,
                site: item.site?.site.id,
                organization: item.space.organization,
                page: '',
                query: undefined,
            };
        case 'page':
        case 'section':
            return {
                type: 'space',
                space: item.space.id,
                site: item.site?.site.id,
                organization: item.space.organization,
                page: item.path,
                query: undefined,
            };
        default:
            assertNever(item);
    }
}

function getKey(item: SearchResultItem): string {
    return item.key;
}

function getGroupKey(
    group: ContentSearchGroupSpace | AskGitBookSearchSuggestion | string | undefined
): string {
    if (typeof group === 'string') {
        return group;
    }

    if (!group) {
        return '';
    }

    if (group.type === 'ask-gitbook') {
        return group.type;
    }

    return group.space.id;
}

function groupSearchItem(
    item: SearchResultItem
): ContentSearchGroupSpace | AskGitBookSearchSuggestionGroup | string | undefined {
    if (item.type === 'ask-gitbook') {
        return {
            type: 'ask-gitbook',
        };
    }

    if (item.type === 'space') {
        return 'Spaces';
    }

    if (item.type === 'page' || item.type === 'link') {
        return 'Pages';
    }

    // Sections are grouped by their parent space
    if (!item.groups) {
        return;
    }

    return item.groups.find((group) => group.type === 'space') as ContentSearchGroupSpace;
}
